import * as React from "react"
import { useDispatch, useSelector } from "react-redux"
import { appActions, userInfoActions } from "../actions"
import { IUserInfo } from "../types/appDataTypes"
import WorkOption from "../components/WorkOption"
import PrimaryButton from "../components/PrimaryButton"
import TextInput, { ITextInputProps } from "../components/TextInput"
import { isRequired } from "../utils/validators"
import * as AppTypes from "AppTypes"
import getStepConfig from "./stepConfig"
import api from "../utils/api"
import { noSymbolFilter } from "../utils/index"
import { updateLanguageAATagging, updateAATagging } from "../utils/aatagging"

type TWorkType = "employed" | "unemployed" | "student"

const WorkStatus: React.FC = () => {
    const dispatch = useDispatch();
    const { userInfo, isCallingApi, langPack } = useSelector((store: AppTypes.ReducerState) => ({
        userInfo: store.userInfo,
        isCallingApi: store.app.isCallingApi,
        langPack: store.config.langPack
    }))

    const [isError, setIsError] = React.useState(false)
    const cluster: ITextInputProps["cluster"] = {}
    const nextStep = async () => {
        let isAllValid = true
        for (let key in cluster) {
            if (!cluster[key]()) isAllValid = false
        }

        if (!userInfo.employmentStatus) {
            isAllValid = false
            setIsError(true)
        }

        if (isAllValid) {
            const response = await api.updateApplication(userInfo)
            if (response && response.status === 200) {
                const { nextStep } = getStepConfig()
                dispatch(appActions.gotoStep(nextStep))

                // AA Tracking
                const AALang = updateLanguageAATagging(langPack["lang"])
                updateAATagging({
                    "event_name": "generic_form_complete_step6",
                    "form_name": "UOB PoY",
                    "step_name": "employment_status",
                    "language": AALang,
                    "country": userInfo.activeCountry.toLowerCase()
                }, "other_form_step")
            }
        }
    }

    const handleChange = (employmentStatus: IUserInfo["employmentStatus"]) => {
        dispatch(userInfoActions.updateUserInfo({ employmentStatus: employmentStatus, nameOfSchool: null, occupation: null }))
        setIsError(false)
    }

    const handleUpdateUserInfo: ITextInputProps["onValidation"] = (validation) => {
        const { isValid, inputId, value } = validation
        if (isValid) {
            dispatch(userInfoActions.updateUserInfo({ [inputId]: value }))
        } else {
            dispatch(userInfoActions.updateUserInfo({ [inputId]: "" }))
        }
    }

    const buttonText = {
        "employed": langPack["labelEmployed"],
        "unemployed": langPack["labelUnemployed"],
        "student": langPack["labelStudent"]
    }

    const workStatusChildren = (employmentStatus: TWorkType) => {
        switch (employmentStatus) {
            case "student":
                return <TextInput
                    inputLabel={langPack["schoolName"]}
                    initValue={userInfo.nameOfSchool}
                    inputId={"nameOfSchool"}
                    validators={[isRequired]}
                    cluster={cluster}
                    onValidation={handleUpdateUserInfo}
                    typingHandler={noSymbolFilter}
                />
            case "employed":
                return <TextInput
                    inputLabel={langPack["labelOccupation"]}
                    initValue={userInfo.occupation}
                    inputId={"occupation"}
                    validators={userInfo.activeCountry === "TH" ? [] : [isRequired]}
                    cluster={cluster}
                    onValidation={handleUpdateUserInfo}
                />
            default:
                return null
        }
    }

    return (
        <React.Fragment>
            <div className="content content--center">
                <h2 className="title">{langPack["employmentStatus"]}</h2>
                <div>
                    {(["employed", "unemployed", "student"] as TWorkType[])
                        .map((employmentStatus) => (
                            <WorkOption
                                key={employmentStatus}
                                text={buttonText[employmentStatus]}
                                selected={userInfo.employmentStatus === employmentStatus}
                                onClick={() => handleChange(employmentStatus)}
                                children={userInfo.employmentStatus === employmentStatus && workStatusChildren(employmentStatus)}
                            />
                        ))}
                </div>
                {isError && (<p className="app-warning app-error-message">{langPack["selectOne"]}</p>)}
            </div>
            <div className="action">
                {langPack["competitionDisclaimer"] &&
                <>
                <p className="app-disclaimer">{langPack["competitionDisclaimerText"]}</p>
                <br />
                </> 
                }
                <PrimaryButton
                    text={langPack["buttonNext"]}
                    onClick={nextStep}
                    disabled={isCallingApi}
                />
            </div>
        </React.Fragment>
    )
}

export default WorkStatus